import { Text, TextField } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import useApi from "system/hooks/use-api";
import usePageTitle from "system/hooks/use-page-title";
import fluentUiClasses, { fluentUiControlStyles, fluentUiDarkTheme } from "system/styles/fluent-ui-classes";
import { IPackageInfo } from "system/types/interfaces/IPackageInfo";
import ActionButtons from "components/ui/ActionButtons";
import ExportToCsvButton from "components/ui/ExportToCsvButton";
import ConnectionErrorPage from "../system/ConnectionErrorPage";
import PackageList from "./PackageList";
import { toDate } from "system/libraries/utilities";

interface TenantPackagesViewProps {
  shortName: string;
}

const TenantPackagesView = ({ shortName }: TenantPackagesViewProps) => {
  const api = useApi();

  usePageTitle({ title: "Packages", subTitle: "" });

  const [packages, setPackages] = useState<IPackageInfo[]>([]);
  const [search, setSearch] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);

  const loadPackages = useCallback(async () => {
    if (!shortName) {
      return;
    }
    setIsLoading(true);
    setHasError(false);
    try {
      const result = await api.packages.getInstalled(shortName);
      setPackages(result?.items ?? []);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [api.packages, shortName]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const filteredPackages = useMemo<IPackageInfo[]>(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return packages;
    }
    return packages.filter((p) => p.name.toLowerCase().includes(term));
  }, [packages, search]);

  const exportData = useMemo(() => {
    const fileName = `Aiden Installed Packages - ${shortName} - ${filteredPackages.length} packages - ${toDate(new Date().toString())}`;
    return {
      fileName,
      source: filteredPackages.map((p) => ({ name: p.name })),
    };
  }, [filteredPackages, shortName]);

  return (
    <div style={{ color: "var(--text-color)" }}>
      <ActionButtons onRefresh={loadPackages}>
        <ExportToCsvButton items={[exportData]} />
      </ActionButtons>
      {hasError ? (
        <ConnectionErrorPage />
      ) : (
        <div className="card-body">
          <Text variant="xLarge" styles={{ root: { color: "var(--title-color)", display: "block", marginBottom: 12 } }}>
            Packages installed in your environment
          </Text>
          <div className={fluentUiClasses.controlWrapper}>
            <TextField
              label="Search Packages:"
              value={search}
              onChange={(_, value) => setSearch(value ?? "")}
              styles={fluentUiControlStyles}
              disabled={isLoading}
              theme={fluentUiDarkTheme}
            />
          </div>
          <PackageList items={filteredPackages} isLoading={isLoading} showSourceColumn={false} />
        </div>
      )}
    </div>
  );
};

export default TenantPackagesView;
